import * as React from "react";
import Button from "@mui/material/Button";
import { makeStyles, styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { Formik, Form, Field, FormikValues } from "formik";
import { MenuItem, TextField } from "@mui/material";
import "../config.styles.css";
import { PLATFORMS } from "../../../constants/configuration.const";
import { useNavigate, useNavigation } from "react-router";
import * as yup from "yup";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(1),
    },
}));

export interface DialogTitleProps {
    id: string;
    children?: React.ReactNode;
    onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

export default function CreateConfigurationPopup({
    show,
    handleCloseCB,
    platforms,
}: any) {
    const [open, setOpen] = React.useState(show);
    const [submitting, setSubmitting] = React.useState(false);
    const navigate = useNavigate();

    // set initial values
    const initialValues: FormikValues = {
        platform: "",
        internalName: "",
        profileName: "",
    };

    const handleClose = () => {
        handleCloseCB();
    };

    // on submit form
    const handleOnSubmit = async (values: FormikValues) => {
        setSubmitting(true);
        navigate(
            `/localization/save-config?profileName=${values.profileName}&internalName=${values.internalName}&platform=${values.platform}`
        );
        setSubmitting(false);
    };

    // validation schema
    const validationSchema = yup.object({
        platform: yup.string().required("Platform should be required"),
        internalName: yup
            .string()
            .required("Internal name is mandatory")
            .matches(/^([a-z]+)-([a-z]+)-([a-z]+)$/),
        profileName: yup.string().required("Profile name is mandatory"),
    });

    return (
        <div>
            {/* This is a popup modal for handling save config */}
            <BootstrapDialog
                fullWidth
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
            >
                <BootstrapDialogTitle
                    id="customized-dialog-title"
                    onClose={handleClose}
                >
                    Create New Configuration
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    {/* Form contents for handling save configuration starts here */}
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleOnSubmit}
                    >
                        {({ values, handleChange, touched, errors }: any) => (
                            <Form className="config-form">
                                <Field
                                    name="platform"
                                    as={TextField}
                                    select
                                    label="Platform"
                                    value={values.platform}
                                    onChange={handleChange}
                                    error={touched.platform && errors.platform}
                                    helperText={
                                        touched.platform && errors.platform
                                    }
                                >
                                    {platforms.map(
                                        (platform: any, index: number) => {
                                            return (
                                                <MenuItem
                                                    key={index}
                                                    value={platform}
                                                >
                                                    {platform}
                                                </MenuItem>
                                            );
                                        }
                                    )}
                                </Field>
                                <Field
                                    name="profileName"
                                    as={TextField}
                                    label="Display Name"
                                    value={values.name}
                                    onChange={handleChange}
                                    error={
                                        touched.profileName &&
                                        errors.profileName
                                    }
                                    helperText={
                                        touched.profileName &&
                                        errors.profileName
                                    }
                                />

                                <Field
                                    name="internalName"
                                    as={TextField}
                                    label="Internal Name i.e., abc-def-ghi"
                                    value={values.internalName}
                                    placeholder="abc-def-ghi"
                                    onChange={handleChange}
                                    error={
                                        touched.internalName &&
                                        errors.internalName
                                    }
                                    helperText={
                                        touched.internalName &&
                                        errors.internalName
                                    }
                                />

                                <Button
                                    className="max-content-width float-center"
                                    variant="outlined"
                                    type="submit"
                                    disabled={submitting}
                                >
                                    Create Config
                                </Button>
                            </Form>
                        )}
                    </Formik>
                    {/* Form contents for handling save configuration ends here */}
                </DialogContent>
            </BootstrapDialog>
        </div>
    );
}
